import {
  createRootRoute,
  createRoute,
  createRouter,
  Outlet,
  redirect,
} from "@tanstack/react-router";
import { DashboardLayout } from "./components/layout/DashboardLayout";
import { requireAuth } from "./lib/route-guards";
import { RootLayout } from "./routes/__root";
import { EntriesPage } from "./routes/dashboard/entries";
import { DashboardPage } from "./routes/dashboard/index";
import { ProfilePage } from "./routes/dashboard/profile";
import { SettingsAuditPage } from "./routes/dashboard/settings/audit";
import { SettingsPage } from "./routes/dashboard/settings/index";
import { SuggestionsPage } from "./routes/dashboard/suggestions";
import { LoginPage } from "./routes/login";

const rootRoute = createRootRoute({ component: RootLayout });

// Single-user app: land on the dashboard, not a login screen — requireAuth on
// dashboardLayout below auto-logs in as the seeded admin. /login is a fallback
// for when auto-login itself fails (DB down, seed never ran).
const indexRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/",
  beforeLoad: () => {
    throw redirect({ to: "/dashboard" });
  },
});

const loginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/login",
  component: LoginPage,
});

function DashboardLayoutRoute() {
  return (
    <DashboardLayout>
      <Outlet />
    </DashboardLayout>
  );
}

const dashboardLayout = createRoute({
  getParentRoute: () => rootRoute,
  path: "/dashboard",
  beforeLoad: requireAuth,
  component: DashboardLayoutRoute,
});

const dashboardIndex = createRoute({
  getParentRoute: () => dashboardLayout,
  path: "/",
  component: DashboardPage,
});

const entriesRoute = createRoute({
  getParentRoute: () => dashboardLayout,
  path: "/entries",
  component: EntriesPage,
});

const suggestionsRoute = createRoute({
  getParentRoute: () => dashboardLayout,
  path: "/suggestions",
  component: SuggestionsPage,
});

const profileRoute = createRoute({
  getParentRoute: () => dashboardLayout,
  path: "/profile",
  component: ProfilePage,
});

// Flat settings — single-user, so no admin gate and no tab layout (that was
// SettingsLayout, now removed). Just the one preference worth surfacing.
const settingsRoute = createRoute({
  getParentRoute: () => dashboardLayout,
  path: "/settings",
  component: SettingsPage,
});

// Kept mounted but unlinked from nav — see .claude/rules/audit.md. Reachable
// by direct URL only.
const settingsAuditRoute = createRoute({
  getParentRoute: () => dashboardLayout,
  path: "/settings/audit",
  component: SettingsAuditPage,
});

const routeTree = rootRoute.addChildren([
  indexRoute,
  loginRoute,
  dashboardLayout.addChildren([
    dashboardIndex,
    entriesRoute,
    suggestionsRoute,
    profileRoute,
    settingsRoute,
    settingsAuditRoute,
  ]),
]);

export const router = createRouter({ routeTree });

declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}
